import { AgeType } from '@eti/schema-types';
import { Inline, Text, vars } from '@etg/wings';
import { useEffect, useState } from 'react';
import { price as priceUtils } from '@eti/utils';
import { Check } from '@phosphor-icons/react';
import { useProperty } from '@eti/providers';
import SmallCardLayout from '../../common/components/small-card-layout/SmallCardLayout';
import { attachDropDownClickListener, sendSnowplowTracking } from '../utils/snowplow';
import { flexibleTicketCtaGridLayoutStyles, infantNoteStyles, textContentStyles } from '../utils/styles';
import ProductLayout from '../../common/main-layout/ProductLayout';
import { useMutationObserver } from '../../custom-hooks/useMutationObserver';
import { useDynamicIcon } from '../../common/utils/dynamic-icons/useDynamicIcon';
import { DYNAMIC_ICON } from '../../common/utils/dynamic-icons/icons';
import { FLEXIBLE_TICKET } from '../../products';
import { formatOptions, getMinPrice, getTotalPrice } from '../../common/utils/selectBoxOptions';
import { ProductCardLayoutType } from '../../common/models/product';
import { useProductData } from '../../common/hook/useProductData';
import { getOptions } from '../utils/flexibleTicketTransformer';
import { isEnabled as isModalEnabled } from '../../common/utils/modal';
import CustomSelectBoxWithModal from '../../common/components/multi-choice-options/CustomSelectBoxWithModal';
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
const {
  name: PRODUCT_NAME,
  id: PRODUCT_ID
} = FLEXIBLE_TICKET;
const {
  Small: SMALL_CARD_LAYOUT,
  Main: MAIN_CARD_LAYOUT,
  PostBooking: POSTBOOKING_CARD_LAYOUT
} = ProductCardLayoutType;
const sharedTexts = {
  adultLabel: 'Adult',
  allTravelersLabel: 'Product.FlexibleTicket.Select.All',
  childLabel: 'Child',
  infantNoteLabel: 'TravelerDetails.IncludedInfant.Text.Default',
  noTravelersLabel: 'Product.FlexibleTicket.Select.None',
  notSelectedLabel: 'Products.Price.Label.From',
  persuasionContent: 'flexibleTicketPersuasionContent',
  placeholder: 'Product.FlexibleTicket.Dropdown.Button.Text',
  placeholderSelectedPlural: 'Product.FlexibleTicket.Dropdown.Label.Selected.Plural',
  placeholderSelectedSingular: 'Product.FlexibleTicket.Dropdown.Label.Selected.Singular',
  readMoreContent: 'flexibleTicketReadMoreModalContent',
  selectboxLabel: 'Product.SelectOneValidation.Message',
  title: 'flexibleTicketHeaderTitle',
  totalLabel: 'Products.Price.Label.Total',
  defaultTravelerLabel: 'TravelerDescription.Header',
  dropDownCloseInstruction: 'ScreenReader.Product.CloseDropdown.Instruction.Text'
};
const mainCardTranslations = {
  ...sharedTexts,
  abstractContent: 'flexibleTicketAbstract',
  upSell: 'flexibleTicketUpSellText'
};
const translations = {
  [MAIN_CARD_LAYOUT]: mainCardTranslations,
  [POSTBOOKING_CARD_LAYOUT]: mainCardTranslations,
  [SMALL_CARD_LAYOUT]: {
    ...sharedTexts,
    abstractContent: 'flexibleTicketAbstractSmallCard',
    upSell: 'flexibleTicketUpSellTextSmallCard'
  }
};
export const SELECTBOX_TEST_ID = `${FLEXIBLE_TICKET.name}-select-box`;
const iconColor = vars.colors.signal.positive.foreground;
const FlexibleTicket = ({
  form,
  order,
  initialState,
  travelerNames,
  onModalConfirm,
  productPosition,
  layoutType = MAIN_CARD_LAYOUT,
  className
}) => {
  var _order$travelers;
  const {
    p
  } = useProperty();
  const [icon] = useDynamicIcon(vars.colors.links.default, DYNAMIC_ICON.linkIcon);
  const hasInfants = order === null || order === void 0 || (_order$travelers = order.travelers) === null || _order$travelers === void 0 ? void 0 : _order$travelers.some(traveler => traveler.ageType === AgeType.Inf);
  const [selectedOptions, setSelectedOptions] = useState(initialState);
  const texts = translations[layoutType] || {};
  useEffect(() => {
    setSelectedOptions(initialState);
  }, [initialState]);
  const {
    productData,
    rootElement,
    translation,
    snowplowTracking: {
      sendClickEvent,
      sendAddToCartEvent,
      updateContext
    }
  } = useProductData({
    productId: PRODUCT_ID,
    order,
    productName: PRODUCT_NAME,
    productPosition,
    isSelected: null,
    form,
    className,
    texts
  });
  const {
    adultLabel,
    allTravelersLabel,
    childLabel,
    infantNoteLabel,
    noTravelersLabel,
    notSelectedLabel,
    selectboxLabel,
    totalLabel,
    defaultTravelerLabel,
    mainCardDescription
  } = translation;
  const options = getOptions({
    id: PRODUCT_ID,
    order,
    travelerNames,
    defaultTravelerLabel,
    adultLabel,
    childLabel
  });
  const productPriceRaw = !(selectedOptions !== null && selectedOptions !== void 0 && selectedOptions.length) ? getMinPrice(options) : getTotalPrice(selectedOptions === null || selectedOptions === void 0 ? void 0 : selectedOptions.filter(option => !(option !== null && option !== void 0 && option.exclude)));
  const formattedOptions = formatOptions(options, allTravelersLabel, noTravelersLabel);
  useMutationObserver({
    targetElement: rootElement,
    selector: "[role='listbox']",
    onElementAdded: element => {
      element.setAttribute('aria-describedby', 'dropdown-close-instruction');
    }
  });
  useEffect(() => {
    const cleanUp = attachDropDownClickListener(rootElement, sendClickEvent);
    return () => {
      cleanUp();
    };
  }, [rootElement, sendClickEvent]);
  if (!productData) {
    return null;
  }
  const isValidationEnabled = p('IbeClient.TravelerDetails.OptionalProductsRequireValidation.Enabled');
  const isPersuasionModalEnabled = isModalEnabled(order.persuasion);
  const extraProps = {
    formattedOptions,
    isPersuasionModalEnabled,
    isValidationEnabled,
    onModalConfirm,
    options,
    selectBoxTestId: SELECTBOX_TEST_ID,
    selectedOptions,
    setSelectedOptions,
    sendAddToCartEvent,
    sendSnowplowTracking,
    translation,
    updateContext
  };
  const updatedProductData = {
    ...productData,
    extraProps,
    price: priceUtils.formatPrice(productPriceRaw),
    priceLabel: !(selectedOptions !== null && selectedOptions !== void 0 && selectedOptions.length) ? notSelectedLabel : totalLabel,
    priceRaw: productPriceRaw !== null && productPriceRaw !== void 0 ? productPriceRaw : 0
  };
  switch (layoutType) {
    case SMALL_CARD_LAYOUT:
      return /*#__PURE__*/_jsx(SmallCardLayout, {
        productData: updatedProductData,
        ref: rootElement,
        children: /*#__PURE__*/_jsx(SmallCardLayout.Cta, {
          customGrid: flexibleTicketCtaGridLayoutStyles(Boolean(selectboxLabel), layoutType === SMALL_CARD_LAYOUT),
          children: /*#__PURE__*/_jsx(CustomSelectBoxWithModal, {})
        })
      });
    case MAIN_CARD_LAYOUT:
    default:
      return /*#__PURE__*/_jsxs(ProductLayout, {
        productData: updatedProductData,
        ref: rootElement,
        children: [/*#__PURE__*/_jsx(ProductLayout.Body, {
          children: /*#__PURE__*/_jsxs(_Fragment, {
            children: [mainCardDescription && /*#__PURE__*/_jsx(Text, {
              asHtml: true,
              className: textContentStyles(icon),
              children: mainCardDescription
            }), hasInfants && infantNoteLabel && /*#__PURE__*/_jsxs(Inline, {
              className: infantNoteStyles,
              noWrap: true,
              spacing: 8,
              children: [/*#__PURE__*/_jsx(Check, {
                color: iconColor,
                "data-testid": "included-checks"
              }), /*#__PURE__*/_jsx(Text, {
                children: infantNoteLabel
              })]
            })]
          })
        }), /*#__PURE__*/_jsx(ProductLayout.Cta, {
          customGrid: flexibleTicketCtaGridLayoutStyles(Boolean(selectboxLabel)),
          children: /*#__PURE__*/_jsx(CustomSelectBoxWithModal, {})
        })]
      });
  }
};
export default FlexibleTicket;
//# sourceMappingURL=FlexibleTicket.js.map